import { useIntl } from "react-intl";

import { useMutation, useQueryClient } from "@tanstack/react-query";

import { Calibrations } from "../../../api/calibrations";
import { MutationKey } from "../../../consts/mutation-keys";
import { QueryKey } from "../../../consts/query-keys";
import { useApiSuccess } from "../../../hooks/useApiSuccess";
import { useFetchError } from "../../../hooks/useFetchError";
import { ApiError } from "../../../types/api-error";

export type UseCalibrationDeleteProps = {
  datasetId: number;
  cafsId: number;
  cafsType: "plant" | "input";
};

export function useCalibrationDelete({
  datasetId,
  cafsId,
  cafsType,
}: UseCalibrationDeleteProps) {
  const { formatMessage } = useIntl();
  const queryClient = useQueryClient();

  const { handleSuccess } = useApiSuccess({
    snackbarText: formatMessage({
      id: "ACTION.CALIBRATION_DELETE_SUCCESS",
      defaultMessage: "Calibration file deleted successfully",
      description: "Calibration delete success",
    }),
  });

  const mutationFn = () => {
    if (cafsType === "plant") {
      return Calibrations.deletePlantCalibration({
        path: { datasetId, plantId: cafsId },
      });
    } else {
      return Calibrations.deleteInputCalibration({
        path: { datasetId, inputId: cafsId },
      });
    }
  };

  const {
    mutate: deleteCalibration,
    isPending,
    error,
  } = useMutation({
    mutationKey: [MutationKey.DeleteCalibration],
    mutationFn: mutationFn,
    onSuccess: () => {
      handleSuccess();
      // Invalidate calibration query to refetch updated data
      queryClient.invalidateQueries({
        queryKey: [QueryKey.Calibration, datasetId, cafsId, cafsType],
      });
    },
  });

  useFetchError(error as unknown as ApiError);

  return { deleteCalibration, isPending };
}
